import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Edit2, Trash2, Mail, MessageSquare, Globe, HelpCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
];

const CATEGORIES = ['general', 'getting_started', 'contributors', 'process', 'ai', 'pricing', 'content', 'privacy', 'shipping', 'support'];

const emptyTranslations = () => ({
  es: { question: '', answer: '' },
  en: { question: '', answer: '' },
  fr: { question: '', answer: '' },
  de: { question: '', answer: '' },
});

const AdminHelpPanel = () => {
  const [tab, setTab] = useState('faqs');
  const [faqs, setFaqs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewTicket, setViewTicket] = useState(null);

  useEffect(() => { load(); }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === 'faqs') {
        const { data } = await axios.get(`${API_URL}/api/admin/faqs`, { withCredentials: true });
        setFaqs(data);
      } else {
        const { data } = await axios.get(`${API_URL}/api/admin/contact-tickets`, { withCredentials: true });
        setTickets(data);
      }
    } catch (e) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing({
      faq_id: null,
      faq_key: '',
      category: 'general',
      order: faqs.length + 1,
      is_active: true,
      translations: emptyTranslations(),
    });
    setEditOpen(true);
  };

  const openEdit = (f) => {
    setEditing({
      ...f,
      translations: { ...emptyTranslations(), ...(f.translations || {}) },
    });
    setEditOpen(true);
  };

  const save = async () => {
    if (!editing.faq_key || !editing.translations.es.question) {
      toast.error('Faq key y pregunta en ES son obligatorios');
      return;
    }
    try {
      if (editing.faq_id) {
        await axios.put(`${API_URL}/api/admin/faqs/${editing.faq_id}`, {
          category: editing.category,
          order: parseInt(editing.order) || 999,
          translations: editing.translations,
          is_active: editing.is_active,
        }, { withCredentials: true });
        toast.success('FAQ actualizada');
      } else {
        await axios.post(`${API_URL}/api/admin/faqs`, editing, { withCredentials: true });
        toast.success('FAQ creada');
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const remove = async (faq) => {
    if (!window.confirm(`¿Eliminar FAQ "${faq.translations?.es?.question || faq.faq_key}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/faqs/${faq.faq_id}`, { withCredentials: true });
      toast.success('FAQ eliminada');
      load();
    } catch (e) {
      toast.error('Error eliminando');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-help-panel">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab('faqs')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'faqs' ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="admin-help-tab-faqs"
        >
          <HelpCircle className="w-4 h-4 inline mr-1" />
          FAQs ({faqs.length})
        </button>
        <button
          onClick={() => setTab('tickets')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'tickets' ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="admin-help-tab-tickets"
        >
          <Mail className="w-4 h-4 inline mr-1" />
          Tickets de contacto ({tickets.length})
        </button>
      </div>

      {tab === 'faqs' && (
        <div>
          <div className="flex justify-end mb-4">
            <Button onClick={openNew} data-testid="admin-faq-new-btn" className="gap-2">
              <Plus className="w-4 h-4" /> Nueva FAQ
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : (
            <div className="space-y-2">
              {faqs.map((f) => (
                <Card key={f.faq_id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-4 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline">{f.category}</Badge>
                        <Badge variant={f.is_active ? "default" : "secondary"}>{f.is_active ? 'Activa' : 'Inactiva'}</Badge>
                        <span className="text-xs text-slate-500">#{f.order}</span>
                        <span className="text-xs text-slate-400">{f.faq_key}</span>
                      </div>
                      <div className="font-medium text-sm text-slate-800">{f.translations?.es?.question || '(sin pregunta ES)'}</div>
                      <div className="text-xs text-slate-500 line-clamp-2 mt-1">{f.translations?.es?.answer}</div>
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {LANGS.map(l => (
                          <span key={l.code} className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${f.translations?.[l.code]?.question ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>{l.label}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(f)} data-testid={`admin-faq-edit-${f.faq_id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(f)} className="text-rose-600 hover:text-rose-700" data-testid={`admin-faq-delete-${f.faq_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'tickets' && (
        <div>
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-10 text-slate-500">Aún no hay mensajes de contacto</div>
          ) : (
            <div className="space-y-2">
              {tickets.map(t => (
                <Card key={t.ticket_id} className="hover:shadow-sm cursor-pointer" onClick={() => setViewTicket(t)}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-1 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{t.category}</Badge>
                        <span className="font-medium text-sm">{t.subject}</span>
                      </div>
                      <span className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-slate-600">{t.name} · {t.email}</div>
                    <div className="text-sm text-slate-700 mt-1 line-clamp-2">{t.message}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAQ edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.faq_id ? 'Editar FAQ' : 'Nueva FAQ'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Faq key</Label>
                  <Input
                    value={editing.faq_key}
                    onChange={(e) => setEditing({ ...editing, faq_key: e.target.value })}
                    disabled={!!editing.faq_id}
                    placeholder="unique_key"
                    data-testid="faq-key-input"
                  />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="faq-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Orden</Label>
                  <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                Activa
              </label>

              {LANGS.map(l => (
                <div key={l.code} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-slate-400" />
                    <span className="font-semibold text-sm">{l.label} ({l.code})</span>
                  </div>
                  <Input
                    placeholder="Pregunta"
                    value={editing.translations[l.code]?.question || ''}
                    onChange={(e) => setEditing({
                      ...editing,
                      translations: { ...editing.translations, [l.code]: { ...editing.translations[l.code], question: e.target.value } },
                    })}
                    data-testid={`faq-question-${l.code}`}
                  />
                  <Textarea
                    placeholder="Respuesta"
                    rows={3}
                    value={editing.translations[l.code]?.answer || ''}
                    onChange={(e) => setEditing({
                      ...editing,
                      translations: { ...editing.translations, [l.code]: { ...editing.translations[l.code], answer: e.target.value } },
                    })}
                    data-testid={`faq-answer-${l.code}`}
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="faq-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket view dialog */}
      <Dialog open={!!viewTicket} onOpenChange={(o) => !o && setViewTicket(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{viewTicket?.subject}</DialogTitle>
          </DialogHeader>
          {viewTicket && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-sm">
                <Badge>{viewTicket.category}</Badge>
                <span className="text-slate-500">{new Date(viewTicket.created_at).toLocaleString()}</span>
              </div>
              <div><strong>De:</strong> {viewTicket.name} &lt;{viewTicket.email}&gt;</div>
              <div className="whitespace-pre-wrap bg-slate-50 rounded-lg p-4 text-sm">{viewTicket.message}</div>
              <Button asChild>
                <a href={`mailto:${viewTicket.email}?subject=Re: ${encodeURIComponent(viewTicket.subject)}`}>
                  <Mail className="w-4 h-4 mr-2" />Responder por email
                </a>
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHelpPanel;
